import { allClimbingPieces, animalPieceTypes } from "../const";
import { allPieceTypes } from "../const";
import { Move } from "../Move";
import {
  IPiece,
  PieceMovement,
  Position,
  PieceType,
  IMove,
  MoveType,
  ISquare,
  EffectType,
  ISideEffect,
  Effect,
} from "../types";
import { BaseMovement, BasicSquareInfo, SquareInfo } from "./BaseMovement";

export class RhinoMovement extends BaseMovement implements PieceMovement {
  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean
  ): IMove[] {
    const possibleMoves: Move[] = [];

    // ZOMBIFICATION: Zombified pieces act as the opposite color
    const effectiveIsLight = piece.zombifiedStatus.isZombified
      ? !piece.isLight
      : piece.isLight;

    // Lane ability - Rhino can swap with ANY adjacent piece left/right on first move
    // Only requirement: Rhino itself hasn't moved yet (adjacent piece's hasMoved doesn't matter)
    if (!piece.hasMoved) {
      const leftPosition = { col: position.col - 1, row: position.row };
      const rightPosition = { col: position.col + 1, row: position.row };
      const leftPiece = this.getPieceAtPosition(leftPosition, boardState);
      const rightPiece = this.getPieceAtPosition(rightPosition, boardState);

      // Check valid lane move for left position
      // Rhino can swap with ANY piece (regardless of whether that piece has moved)
      if (this.isValidLaneMove(piece, leftPosition, boardState, effectiveIsLight)) {
        possibleMoves.push(
          new Move(piece, leftPosition, MoveType.lane, leftPiece)
        );
      }

      // Check valid lane move for right position
      // Rhino can swap with ANY piece (regardless of whether that piece has moved)
      if (this.isValidLaneMove(piece, rightPosition, boardState, effectiveIsLight)) {
        possibleMoves.push(
          new Move(piece, rightPosition, MoveType.lane, rightPiece)
        );
      }
    }

    // Regular movement
    // CRITICAL: Use effectiveIsLight so zombified pieces move in the opposite direction
    // Zombified Dark Rhino (controlled by Light) should move like a Light Rhino (forward = up)
    const forwardDirection = effectiveIsLight ? 1 : -1;

    // Check the two-square forward position first
    const twoSquaresForwardPosition = {
      col: position.col,
      row: position.row + forwardDirection * 2,
    };

    // Check the one-square forward position as fallback
    const oneSquareForwardPosition = {
      col: position.col,
      row: position.row + forwardDirection,
    };

    // First, check the two-square move

    if (this.onBoard(boardState, twoSquaresForwardPosition)) {
      const twoSquaresForwardSquare =
        boardState[twoSquaresForwardPosition.row][
          twoSquaresForwardPosition.col
        ];
      const twoSquaresForwardInfo = this.getTrampleSquareInfo(
        twoSquaresForwardSquare,
        effectiveIsLight
      );

      if (twoSquaresForwardInfo.basicSquareInfo) {
        const move = this.getMove(
          position,
          twoSquaresForwardPosition,
          twoSquaresForwardInfo.basicSquareInfo,
          piece,
          allPieceTypes
        );

        if (move) {
          const oneSquareAhead =
            boardState[twoSquaresForwardPosition.row - forwardDirection][
              twoSquaresForwardPosition.col
            ];
          move.addSideEffect(
            {
              row: twoSquaresForwardPosition.row - forwardDirection,
              col: twoSquaresForwardPosition.col,
            },
            oneSquareAhead.Pieces,
            Effect.Capture()
          );
          possibleMoves.push(move);
        }
      }
    } else {
      // If two-square move is off the board, check the one-square move
      if (this.onBoard(boardState, oneSquareForwardPosition)) {
        const oneSquareForwardSquare =
          boardState[oneSquareForwardPosition.row][
            oneSquareForwardPosition.col
          ];
        const oneSquareForwardInfo = this.getTrampleSquareInfo(
          oneSquareForwardSquare,
          effectiveIsLight
        );

        if (oneSquareForwardInfo.basicSquareInfo) {
          const move = this.getMove(
            position,
            oneSquareForwardPosition,
            oneSquareForwardInfo.basicSquareInfo,
            piece,
            allPieceTypes
          );

          if (move) {
            possibleMoves.push(move);
          }
        }
      }
    }

    const backLeftPosition = {
      col: position.col - 1,
      row: position.row - forwardDirection,
    };
    const backRightPosition = {
      col: position.col + 1,
      row: position.row - forwardDirection,
    };

    if (this.onBoard(boardState, backLeftPosition)) {
      const backLeftSquare =
        boardState[backLeftPosition.row][backLeftPosition.col];
      const backLeftInfo = this.getTrampleSquareInfo(backLeftSquare, effectiveIsLight);

      if (backLeftInfo.basicSquareInfo) {
        const move = this.getMove(
          position,
          backLeftPosition,
          backLeftInfo.basicSquareInfo,
          piece,
          allPieceTypes
        );

        if (move) {
          possibleMoves.push(move);
        }
      }
    }

    if (this.onBoard(boardState, backRightPosition)) {
      const backRightSquare =
        boardState[backRightPosition.row][backRightPosition.col];
      const backRightInfo = this.getTrampleSquareInfo(
        backRightSquare,
        effectiveIsLight
      );

      if (backRightInfo.basicSquareInfo) {
        const move = this.getMove(
          position,
          backRightPosition,
          backRightInfo.basicSquareInfo,
          piece,
          allPieceTypes
        );

        if (move) {
          possibleMoves.push(move);
        }
      }
    }

    // Mega charge
    if (!piece.hasCharged) {
      // Debug: Log all shadow pieces on the board
      const shadowPositions: { row: number; col: number; pieces: any[] }[] = [];
      for (let r = 0; r < boardState.length; r++) {
        for (let c = 0; c < boardState[r].length; c++) {
          const shadowPieces = boardState[r][c].Pieces.filter(
            (p) => p.isShadow
          );
          if (shadowPieces.length > 0) {
            shadowPositions.push({
              row: r,
              col: c,
              pieces: shadowPieces.map((p) => ({
                id: p.id,
                type: p.type,
                isShadow: p.isShadow,
              })),
            });
          }
        }
      }
     

      // Updated: Check all three squares ahead (including the destination)
      const chargePositions = [
        { col: position.col, row: position.row + forwardDirection },
        { col: position.col, row: position.row + forwardDirection * 2 },
        { col: position.col, row: position.row + forwardDirection * 3 },
      ];
      let pathClear = true;
      let foundAnimalOrShadow = false;
      for (const pos of chargePositions) {
        if (!this.onBoard(boardState, pos)) {
          pathClear = false;
          break;
        }
        const square = boardState[pos.row][pos.col];
        if (square.Pieces.length === 0) {
          continue;
        }
        if (
          square.Pieces.some(
            (p) => p.isShadow === true || animalPieceTypes.includes(p.type)
          )
        ) {
          foundAnimalOrShadow = true;
          continue;
        }
        // Otherwise, path is blocked
        pathClear = false;
        break;
      }
      const endPosition = chargePositions[2];
      if (pathClear && foundAnimalOrShadow) {
        const endSquare = boardState[endPosition.row][endPosition.col];
        const endInfo = this.getTrampleSquareInfo(endSquare, effectiveIsLight);
        if (endInfo.basicSquareInfo) {
          const move = this.getMove(
            position,
            endPosition,
            endInfo.basicSquareInfo,
            piece,
            allPieceTypes, // Rhino can capture any piece type
            true // This is a charge move
          );
          if (move) {
           
            possibleMoves.push(move);
          }
        }
      } else {
       
      }
    }

    // Capture the rider through the normal pipeline (preserving poison/shed),
    // and the Tree through the capture effects already used by trample paths.
    for (const move of possibleMoves) {
      if (move.moveType !== MoveType.trample) continue;
      const occupants = boardState[move.position.row][move.position.col].Pieces;
      if (!occupants.some(p => p.type === PieceType.Tree)) continue;
      const rider = occupants.find(p => p.type !== PieceType.Tree && p.isHousing);
      if (!rider) continue;
      move.toPiece = rider;
      move.addSideEffect(move.position, occupants.filter(p => p !== rider), Effect.Capture());
    }

    // FRIENDLY FIRE PREVENTION: Filter out moves that would capture allied rhinos
    // Exception: Allow friendly trampling during charge moves (3-square forward moves)
    // console.log(`🛡️ FRIENDLY FIRE CHECK: Rhino at (${position.row},${position.col}) checking ${possibleMoves.length} possible moves`);
    const filteredMoves = possibleMoves.filter(move => {
      const targetSquare = boardState[move.position.row][move.position.col];
      const targetPieces = targetSquare.Pieces;
      
      // Check if target square has an allied rhino
      const hasAlliedRhino = targetPieces.some(p => 
        p.type === PieceType.Rhino && p.isLight === effectiveIsLight
      );
    
      
      if (!hasAlliedRhino) {
        return true; // Safe move, no allied rhino at target
      }
      
      // If there's an allied rhino, only allow if this is a charge move
      const isChargeMove = Math.abs(move.position.row - position.row) === 3;
      
      if (isChargeMove) {
        // Allow charge moves to trample allied rhinos (existing game mechanic)
        return true;
      } else {
    
        return false;
      }
    });

    // console.log(`✅ FRIENDLY FIRE FILTER: Reduced moves from ${possibleMoves.length} to ${filteredMoves.length}`);
    return filteredMoves;
  }

  // Only Rhino trample destinations unwrap occupied Trees. Lane swaps and
  // other pieces retain BaseMovement's existing housing rules.
  private getTrampleSquareInfo(square: ISquare, isLight: boolean): SquareInfo {
    const info = this.getSquareInfo(square, isLight);
    if (!info.basicSquareInfo && info.housedTreeSquareInfo) {
      const tree = square.Pieces.find(p => p.type === PieceType.Tree);
      if (tree) return { ...info, basicSquareInfo: this.getBasicInfo({ Pieces: [tree] }, isLight) };
    }
    return info;
  }

  private isValidLaneMove(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean
  ): boolean {
    if (!this.onBoard(boardState, position)) return false;

    const squareInfo = this.getSquareInfo(
      boardState[position.row][position.col],
      effectiveIsLight
    );

    // Lane move requires:
    // 1. Square has a piece
    // 2. Piece is not a Rhino
    // IMPORTANT: Lane works with ALLIES (friendly pieces), not enemies!
    return (
      squareInfo.basicSquareInfo !== null &&
      !squareInfo.basicSquareInfo.isEmpty &&
      squareInfo.basicSquareInfo.piece?.type !== PieceType.Rhino
    );
  }

  private isOccupiedByAnimalPiece(
    position: Position,
    boardState: ISquare[][],
    isLight: boolean
  ): boolean {
    if (!this.onBoard(boardState, position)) return false;

    const squareInfo = this.getSquareInfo(
      boardState[position.row][position.col],
      isLight
    );

    const p = squareInfo.basicSquareInfo?.piece;
    return (
      !!squareInfo.basicSquareInfo &&
      !squareInfo.basicSquareInfo.isEmpty &&
      !!p &&
      (p.isShadow === true || animalPieceTypes.includes(p.type))
    );
  }

  private getPieceAtPosition(
    position: Position,
    boardState: ISquare[][]
  ): IPiece | null {
    if (!this.onBoard(boardState, position)) return null;
    const square = boardState[position.row][position.col];
    return square.Pieces[0] || null;
  }

  // Override getMove to always use trample for Rhino moves
  getMove(
    fromPosition: Position,
    toPosition: Position,
    basicSquareInfo: BasicSquareInfo,
    piece: IPiece,
    capturablePieceTypes: PieceType[],
    isCharge: boolean = false
  ): Move | undefined {
    // Allow Rhino to trample (capture) shadow pieces
    if (basicSquareInfo.piece && basicSquareInfo.piece.isShadow === true) {
      return new Move(
        piece,
        toPosition,
        MoveType.trample,
        basicSquareInfo.piece
      );
    }
    // For Rhino, we want all moves to be trample
    if (basicSquareInfo.isEmpty) {
      return new Move(piece, toPosition, MoveType.trample, null);
    } else if (
      basicSquareInfo.isEnemyOccupied &&
      capturablePieceTypes.includes(basicSquareInfo.piece!.type)
    ) {
      return new Move(
        piece,
        toPosition,
        MoveType.trample,
        basicSquareInfo.piece
      );
    } else if (
      basicSquareInfo.isAlly &&
      basicSquareInfo.piece?.type === PieceType.Tree &&
      allClimbingPieces.includes(piece.type)
    ) {
      return new Move(piece, toPosition, MoveType.house, basicSquareInfo.piece);
    } else if (
      basicSquareInfo.isAlly &&
      basicSquareInfo.piece?.type !== PieceType.Tree &&
      capturablePieceTypes.includes(basicSquareInfo.piece!.type) &&
      isCharge
    ) {
      // Allow trampling allied pieces ONLY for charge moves
      return new Move(
        piece,
        toPosition,
        MoveType.trample,
        basicSquareInfo.piece
      );
    }

    // If the square can't be moved to
    return undefined;
  }
}
